import React, { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CreditCard, Loader2 } from 'lucide-react'
import { Input as ShadcnInput } from '../ui/Input'
import { RadioGroup, RadioGroupItem } from '../ui/RadioGroup'
import { Label } from '../ui/label'
import { cn } from '@/lib/utils'

interface DocumentInputProps {
  value: string
  onChange: (value: string, type: 'cpf' | 'cnpj') => void
  onBlur?: () => void
  error?: string
  label?: string
  placeholder?: string
  personType?: 'pf' | 'pj'
  onPersonTypeChange?: (type: 'pf' | 'pj') => void
  validationMessage?: string | null
  isValidating?: boolean
  required?: boolean
}

export const DocumentInput: React.FC<DocumentInputProps> = ({
  value,
  onChange,
  onBlur,
  error,
  label,
  placeholder,
  personType: externalPersonType,
  onPersonTypeChange,
  validationMessage,
  isValidating,
  required,
}) => {
  const { t } = useTranslation()
  const inputId = useId()
  const helpId = `${inputId}-help`
  const errorId = `${inputId}-error`
  const [internalPersonType, setInternalPersonType] = useState<'pf' | 'pj'>('pf')
  const hasManualPersonTypeSelection = useRef(false)
  const hasUserEditedDocument = useRef(false)

  const personType = externalPersonType ?? internalPersonType

  const handlePersonTypeChange = (type: 'pf' | 'pj') => {
    hasManualPersonTypeSelection.current = true
    if (onPersonTypeChange) {
      onPersonTypeChange(type)
    } else {
      setInternalPersonType(type)
    }
    onChange('', type === 'pf' ? 'cpf' : 'cnpj')
  }

  useEffect(() => {
    if (externalPersonType || hasManualPersonTypeSelection.current || hasUserEditedDocument.current) return
    const numbers = value.replace(/\D/g, '')
    if (!numbers) return

    if (numbers.length > 11) {
      if (onPersonTypeChange) {
        onPersonTypeChange('pj')
      } else {
        setInternalPersonType('pj')
      }
    }
  }, [value, externalPersonType, onPersonTypeChange])

  const formatCPF = (input: string): string => {
    const numbers = input.replace(/\D/g, '').substring(0, 11)
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
  }

  const formatCNPJ = (input: string): string => {
    const numbers = input.replace(/\D/g, '').substring(0, 14)
    return numbers
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    hasUserEditedDocument.current = true
    const input = e.target.value
    const maxLength = personType === 'pf' ? 14 : 18

    if (personType === 'pf') {
      const formatted = formatCPF(input)
      if (formatted.length <= maxLength) {
        onChange(formatted, 'cpf')
      }
    } else {
      const formatted = formatCNPJ(input)
      if (formatted.length <= maxLength) {
        onChange(formatted, 'cnpj')
      }
    }
  }

  const documentNumbers = value.replace(/\D/g, '')
  const requiredDigits = personType === 'pf' ? 11 : 14
  const validationCopy = validationMessage?.trim() ?? ''
  const validationSucceeded =
    validationCopy.toLowerCase().includes('válido') ||
    (validationCopy.toLowerCase().includes('valid') &&
      !validationCopy.toLowerCase().includes('invalid'))

  const inputPlaceholder =
    placeholder ??
    (personType === 'pf'
      ? t('leads.form.documentCpfPlaceholder', '000.000.000-00')
      : t('leads.form.documentCnpjPlaceholder', '00.000.000/0000-00'))

  const inputDescriptionId = (isValidating || validationCopy || documentNumbers.length < requiredDigits)
    ? helpId
    : undefined
  const describedBy = [inputDescriptionId, error ? errorId : undefined].filter(Boolean).join(' ') || undefined

  const renderHelpText = () => {
    if (isValidating) {
      return (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" id={helpId}>
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>{t('leads.form.checkingDocument', 'Verificando documento...')}</span>
        </div>
      )
    }

    if (validationCopy) {
      return (
        <p
          id={helpId}
          className={cn(
            'flex items-center gap-2 text-xs font-medium',
            validationSucceeded
              ? 'text-emerald-600 dark:text-emerald-400'
              : 'text-muted-foreground'
          )}
        >
          {validationSucceeded && <span aria-hidden="true">✓</span>}
          <span>{validationCopy}</span>
        </p>
      )
    }

    if (documentNumbers.length < requiredDigits) {
      return (
        <p id={helpId} className="text-xs text-muted-foreground">
          {personType === 'pf'
            ? t('leads.form.documentCpfHint', 'Digite 11 dígitos para CPF')
            : t('leads.form.documentCnpjHint', 'Digite 14 dígitos para CNPJ')
          } ({documentNumbers.length}/{requiredDigits})
        </p>
      )
    }

    return null
  }

  return (
    <div className="space-y-4">
      {(label || placeholder) && (
        <div className="space-y-1">
          {label && (
            <div>
              <Label htmlFor={inputId} className="text-sm font-medium text-foreground">
                {label}
              </Label>
              {required && <span className="ml-1 text-sm text-destructive" aria-hidden="true">*</span>}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            {t('leads.form.documentTypeHint', 'Escolha o tipo de documento antes de informar o número.')}
          </p>
        </div>
      )}

      <RadioGroup
        value={personType}
        onValueChange={(val) => handlePersonTypeChange(val as 'pf' | 'pj')}
        className="grid grid-cols-1 gap-2 sm:grid-cols-2"
      >
        <RadioGroupItem
          value="pf"
          id={`${inputId}-pf`}
          className={cn(
            'rounded-xl border px-4 py-3 transition-all duration-200',
            personType === 'pf'
              ? 'border-accent bg-accent/5 text-foreground shadow-sm'
              : 'border-border bg-card text-muted-foreground hover:border-accent/30 hover:bg-muted/50 hover:text-foreground'
          )}
          labelClassName={cn(
            'text-sm font-medium',
            personType === 'pf' ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          {t('leads.form.personTypePF', 'Pessoa Física')}
        </RadioGroupItem>
        <RadioGroupItem
          value="pj"
          id={`${inputId}-pj`}
          className={cn(
            'rounded-xl border px-4 py-3 transition-all duration-200',
            personType === 'pj'
              ? 'border-accent bg-accent/5 text-foreground shadow-sm'
              : 'border-border bg-card text-muted-foreground hover:border-accent/30 hover:bg-muted/50 hover:text-foreground'
          )}
          labelClassName={cn(
            'text-sm font-medium',
            personType === 'pj' ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          {t('leads.form.personTypePJ', 'Pessoa Jurídica')}
        </RadioGroupItem>
      </RadioGroup>

      <div className="relative">
        <CreditCard className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <ShadcnInput
          id={inputId}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={inputPlaceholder}
          maxLength={personType === 'pf' ? 14 : 18}
          className={cn(
            'h-10 rounded-xl pl-10 shadow-sm',
            error && 'border-destructive focus-visible:ring-destructive'
          )}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          title={personType === 'pf'
            ? t('leads.form.documentCpfTitle', 'CPF - Cadastro de Pessoa Física')
            : t('leads.form.documentCnpjTitle', 'CNPJ - Cadastro Nacional de Pessoa Jurídica')
          }
        />
      </div>

      {renderHelpText()}

      {error && (
        <p id={errorId} className="rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm font-medium text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}
